import { HAK_CIPTA } from '../components/AppFooter';
import { LogoLensa } from '../components/LogoLensa';
import { isAdmin, useAuth } from '../hooks/useAuth';
import { useBahasa } from '../i18n';

/**
 * About (`/about`) — 1 September 2026, diisi sore harinya.
 *
 * Lahir sebagai kerangka (*"kosongan aja atau template dulu"*); isinya
 * diserahkan user beberapa jam kemudian, jadi blok "menyusul" yang bergaris
 * putus-putus sudah tidak ada lagi.
 *
 * **Terbuka untuk semua peran.** Tidak ada satu pun angka sistem di sini — yang
 * itu urusan `/status` (§G9), dan halaman ini sengaja tidak menyentuhnya:
 * "tentang aplikasi" yang berisi kesehatan disk adalah halaman diagnostik yang
 * menyamar.
 */

/**
 * Versi produk. Ditulis di satu tempat, disamakan tangan dengan
 * `frontend/package.json` — bukan diimpor dari sana: mengimpor `package.json`
 * ke dalam bundel ikut membawa seluruh daftar dependensi ke browser.
 */
const VERSI = '0.1.0';

/**
 * Kapan daftar komponen di bawah terakhir diperiksa terhadap yang benar-benar
 * terpasang di box ini.
 *
 * Wajib ada, dan wajib ikut diperbarui bersama daftarnya. Daftar versi tanpa
 * tanggal periksa adalah daftar yang perlahan jadi bohong tanpa satu orang pun
 * bisa tahu sejak kapan — dan yang membacanya justru orang yang sedang
 * memeriksa lisensi atau kerentanan.
 */
const DIPERIKSA = '1 September 2026';

interface Komponen {
  nama: string;
  versi: string;
  lisensi: string;
  /**
   * Satu kalimat, hanya kalau memang ada yang perlu dijelaskan.
   *
   * Ditulis dalam bahasa Inggris dan diterjemahkan saat dirender, sama seperti
   * seluruh teks pemakai lain (§H9) — daftar ini konstanta modul, jadi ia tidak
   * bisa memanggil `t()` sendiri, dan itu yang dulu membuat dua kalimat di sini
   * tetap berbahasa Indonesia walau antarmukanya sedang bahasa Inggris.
   */
  catatan?: string;
}

/**
 * Yang benar-benar terpasang di box ini — dibaca dari `node_modules` dan dari
 * paket sistem, bukan disalin dari `package.json`. Keduanya bisa berbeda:
 * `^18.3.1` yang tertulis di manifest terpasang sebagai 18.3.1 hari ini dan
 * sebagai 18.4 bulan depan, dan yang ditanya orang yang membaca halaman ini
 * selalu yang kedua.
 *
 * Paket `@types/*` sengaja tidak didaftar: isinya deklarasi tipe TypeScript
 * saja, tidak ada satu baris pun yang ikut ke berkas hasil build, jadi
 * mencantumkannya cuma memanjangkan daftar yang justru harus bisa dibaca.
 */
const RUNTIME: Komponen[] = [
  { nama: 'Node.js', versi: '20.20.2', lisensi: 'MIT' },
  { nama: 'MariaDB', versi: '10.11.6', lisensi: 'GPL-2.0' },
  { nama: 'Redis', versi: '7.0.15', lisensi: 'BSD-3-Clause' },
  {
    nama: 'FFmpeg',
    versi: '5.1.8',
    lisensi: 'GPL-2.0-or-later',
    catatan: 'Called as a separate process, not linked into Lensa.',
  },
  { nama: 'Inter', versi: 'variable', lisensi: 'SIL OFL 1.1', catatan: 'Interface font.' },
];

const BACKEND: Komponen[] = [
  { nama: 'fastify', versi: '5.12.0', lisensi: 'MIT' },
  { nama: '@fastify/cookie', versi: '11.1.2', lisensi: 'MIT' },
  { nama: '@fastify/rate-limit', versi: '10.3.0', lisensi: 'MIT' },
  { nama: '@fastify/static', versi: '8.3.0', lisensi: 'MIT' },
  { nama: 'prisma / @prisma/client', versi: '6.19.3', lisensi: 'Apache-2.0' },
  { nama: 'ioredis', versi: '5.11.1', lisensi: 'MIT' },
  { nama: 'ldapts', versi: '7.4.0', lisensi: 'MIT' },
  { nama: 'argon2', versi: '0.41.1', lisensi: 'MIT' },
  { nama: 'bcryptjs', versi: '3.0.3', lisensi: 'BSD-3-Clause' },
  { nama: 'pino', versi: '9.14.0', lisensi: 'MIT' },
  { nama: 'pino-roll', versi: '3.1.0', lisensi: 'MIT' },
  { nama: 'zod', versi: '3.25.76', lisensi: 'MIT' },
];

const FRONTEND: Komponen[] = [
  { nama: 'react', versi: '18.3.1', lisensi: 'MIT' },
  { nama: 'react-dom', versi: '18.3.1', lisensi: 'MIT' },
  { nama: 'react-router-dom', versi: '6.30.4', lisensi: 'MIT' },
  { nama: 'tailwindcss', versi: '3.4.19', lisensi: 'MIT' },
  { nama: 'postcss', versi: '8.5.26', lisensi: 'MIT' },
  { nama: 'autoprefixer', versi: '10.5.4', lisensi: 'MIT' },
];

const PERKAKAS: Komponen[] = [
  { nama: 'typescript', versi: '5.9.3', lisensi: 'Apache-2.0' },
  { nama: 'vite', versi: '6.4.3', lisensi: 'MIT' },
  { nama: '@vitejs/plugin-react', versi: '4.7.0', lisensi: 'MIT' },
  { nama: 'tsx', versi: '4.23.12', lisensi: 'MIT' },
];

/** Satu baris fakta di kotak identitas. */
function Baris({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-t border-line px-4 py-2.5 first:border-t-0">
      <dt className="w-40 shrink-0 text-sm text-muted">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm">{children}</dd>
    </div>
  );
}

/**
 * Nama perusahaan sebagai tautan ke situsnya.
 *
 * Dibuka di tab baru, mengikuti kebijakan yang sama dengan tautan setelan kamera
 * di halaman Cameras: halaman ini dibuka dari konsol yang sedang dipakai
 * memantau, dan menggantikan tab itu dengan situs perusahaan berarti menutup
 * pantauan yang sedang berjalan — di pos satpam belum tentu ada yang tahu cara
 * mengembalikannya.
 *
 * Diberi warna aksen dan garis bawah, bukan sekadar teks yang kebetulan bisa
 * diklik: di dalam daftar fakta yang seluruh isinya teks datar, tautan yang
 * tidak menandai dirinya sendiri tidak akan pernah ditemukan.
 *
 * `rel="noreferrer noopener"` bukan formalitas: tanpa `noopener`, halaman yang
 * dibuka mewarisi `window.opener` dan bisa mengarahkan tab konsol ini ke mana
 * pun tanpa sepengetahuan yang membukanya.
 */
function TautanPerusahaan({ nama, situs }: { nama: string; situs: string }) {
  const { t } = useBahasa();
  return (
    <a
      href={`https://${situs}`}
      target="_blank"
      rel="noreferrer noopener"
      title={t('Open {situs} in a new tab', { situs })}
      className="rounded text-accent-text underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      {nama}
    </a>
  );
}

/**
 * Satu kelompok komponen.
 *
 * Daftar, bukan `<table>`: yang dibaca di sini tiga hal per baris, dan tabel
 * tiga kolom pada layar 390px memaksa salah satu kolomnya membungkus jadi dua
 * baris — biasanya justru kolom nama. Nama dan versi menempel jadi satu kesatuan
 * di kiri, lisensinya menggantung di kanan, dan pada layar sempit lisensinya
 * turun sendiri tanpa memotong apa pun.
 */
function Kelompok({ judul, isi }: { judul: string; isi: Komponen[] }) {
  const { t } = useBahasa();
  return (
    <section className="mt-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">{judul}</h3>
      <ul className="mt-1.5 overflow-hidden rounded-lg border border-line bg-surface">
        {isi.map((k) => (
          <li
            key={k.nama}
            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-t border-line px-4 py-2 first:border-t-0"
          >
            <span className="min-w-0 text-sm">
              <span className="font-mono">{k.nama}</span>{' '}
              <span className="tabular-nums text-muted">{k.versi}</span>
              {k.catatan && <span className="block text-xs text-muted">{t(k.catatan)}</span>}
            </span>
            <span className="shrink-0 font-mono text-xs text-muted">{k.lisensi}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function AboutPage() {
  const { t } = useBahasa();
  const { user } = useAuth();
  /**
   * Daftar komponen pihak ketiga: **admin saja** sejak 8 September 2026.
   *
   * Diminta lapangan: *"di About ada third party component, kayaknya user
   * jangan sampai tahu juga, itu ngebeberin semua dapur kita. Gampang banget
   * ditiru nanti kalau ke depannya mau dijual ke orang."*
   *
   * Dibatasi, bukan dihapus. Daftar itu punya guna yang tidak hilang: sebagian
   * lisensi yang dipakai Lensa menuntut atribusi ikut terdistribusi bersama
   * perangkat lunaknya, dan menghapusnya dari produk berarti kehilangan
   * satu-satunya tempat atribusi itu tinggal. Admin tetap bisa membukanya —
   * dialah yang mengaudit lisensi — sementara pemakai biasa tidak lagi
   * disodori peta lengkap isi dapurnya.
   */
  const admin = isAdmin(user);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex flex-wrap items-center gap-4">
        <LogoLensa tinggi="h-9 w-auto" alt="Lensa" />
        <p className="text-sm text-muted">{t('Every camera, one screen.')}</p>
      </div>

      <dl className="mt-6 overflow-hidden rounded-lg border border-line bg-surface">
        <Baris label={t('Product')}>Lensa — {t('Video Surveillance')}</Baris>
        <Baris label={t('Version')}>
          <span className="tabular-nums">{VERSI}</span>
        </Baris>
        <Baris label={t('Published by')}>
          <TautanPerusahaan nama="PT. Sandya Sistem Indonesia" situs="sandya.net.id" />
        </Baris>
        <Baris label={t('Support')}>
          <TautanPerusahaan nama="PT. Gelombang Jejaring Solusi" situs="gelombang.io" />
        </Baris>
        {/* Terisi 2 September 2026, menggantikan "Belum diisi." yang sengaja
            dibiarkan berdiri sebagai penanda bahwa barisnya masih menunggu.
            Keterangannya digantung sebagai baris kedua yang teredam, pola yang
            sama dengan catatan di daftar komponen di bawah: yang dicari mata
            adalah bentuk lisensinya, bukan syaratnya. */}
        <Baris label={t('Licence')}>
          {t('Proprietary Software — Free to Use')}
          <span className="block text-xs text-muted">{t('Terms and conditions apply')}</span>
        </Baris>
      </dl>

      <section className="mt-8">
        <h2 className="text-sm font-semibold">{t('About this application')}</h2>
        <p className="mt-1 text-sm leading-relaxed">
          {t(
            'LENSA is a video surveillance platform for centralised monitoring, recording, playback, and camera management.',
          )}
        </p>
      </section>

      {/* Terlipat, dan tertutup saat halaman dibuka.
          Dua puluh tujuh baris versi adalah bagian terpanjang halaman ini
          sekaligus yang paling jarang dibaca — ia dibuka saat seseorang
          mengaudit lisensi, mungkin sekali setahun. Dibentang terus, ia
          menenggelamkan blok Kontak sejauh satu setengah layar ke bawah,
          padahal justru itu yang dicari orang yang membuka halaman "About".

          `<details>` bawaan, bukan disclosure buatan sendiri: ia sudah bisa
          dibuka papan ketik, sudah diumumkan pembaca layar sebagai bagian yang
          bisa dibentang, dan **isinya tetap ketemu Ctrl+F** di browser modern —
          tiga hal yang harus ditulis ulang satu per satu kalau memakai
          `useState` dan sebuah `<div>`. */}
      {admin && (
      <details className="group mt-8">
        <summary className="flex cursor-pointer list-none items-center gap-2 rounded text-sm font-semibold marker:content-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
          <span
            aria-hidden="true"
            className="text-muted transition-transform duration-150 group-open:rotate-90 motion-reduce:transition-none"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </span>
          {t('Third-party components')}
          {/* Jumlahnya disebut di ringkasannya: bagian terlipat yang tidak
              mengatakan seberapa banyak isinya membuat orang membukanya sekadar
              untuk mengetahui apakah perlu dibuka. */}
          <span className="font-normal tabular-nums text-muted">
            {RUNTIME.length + BACKEND.length + FRONTEND.length + PERKAKAS.length}
          </span>
        </summary>

        <p className="mt-1 text-sm text-muted">
          {t('Installed on this server, checked on {tgl}.', { tgl: DIPERIKSA })}
        </p>

        <Kelompok judul={t('Runtime and infrastructure')} isi={RUNTIME} />
        <Kelompok judul={t('Backend')} isi={BACKEND} />
        <Kelompok judul={t('Frontend')} isi={FRONTEND} />
        <Kelompok judul={t('Build tooling')} isi={PERKAKAS} />
      </details>
      )}

      <section className="mt-8">
        <h2 className="text-sm font-semibold">{t('Contact')}</h2>
        <address className="mt-2 rounded-lg border border-line bg-surface px-4 py-3 text-sm not-italic leading-relaxed">
          <p>Ahmad Zaki M. Lubis, Muhamad Farhan</p>
          <p className="text-muted">PT. Sandya Sistem Indonesia ©2026</p>
          <p className="mt-2">
            Jl. Sapta Taruna Raya Blok Sakura Lt. 1 No. 5, Pondok Pinang, Jakarta Selatan, 12310
          </p>
          {/* Nomor telepon dan email jadi tautan: halaman ini paling sering
              dibuka justru dari ponsel, dan nomor yang harus disalin tangan
              adalah nomor yang tidak jadi ditelepon. */}
          <p className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
            <a href="tel:+622139721212" className="tabular-nums hover:underline">
              +62 21 39721212
            </a>
            <a href="tel:+628111635101" className="tabular-nums hover:underline">
              +62 8111635101
            </a>
          </p>
          <p>
            <a href="mailto:dev@sandya.net.id" className="hover:underline">
              dev@sandya.net.id
            </a>
          </p>
        </address>
      </section>

      <p className="mt-6 text-xs text-muted">{HAK_CIPTA}</p>
    </div>
  );
}
